import { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import AddMaterialModal from "./AddMaterialModal";
import AddCategoryModal from "./AddCategoryModal";
import EditMaterialModal from "./EditMaterialModal";
import EditCategoryModal from "./EditCategoryModal";
import { useAuth } from "../../../src/Context/AuthContext"

const AdminMaterialsSection = () => {
    const [materials, setMaterials] = useState([]);
    const [categories, setCategories] = useState([]);
    const [showAddMaterialModal, setShowAddMaterialModal] = useState(false);
    const [editingMaterial, setEditingMaterial] = useState(null);
    const [editingCategory, setEditingCategory] = useState(null);
    const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
    const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
    const { logout } = useAuth();

    const apiURL = import.meta.env.VITE_API;

    const navigate = useNavigate();

    useEffect(() => {
        fetchMaterials();
        fetchCategories();
    }, []);

    //-----------------------------------------------------------------------------------------------------------------

    const fetchMaterials = async () => {
        try {
            const res = await fetch(`${apiURL}/materials/all-materials`, {
                method: "GET",
                credentials: "include"
            });

            if (res.status === 401) {
                logout();
                navigate('/login');
                throw new Error("unauthorized");
            }

            if (res.status === 403) {
                navigate('/home');
                throw new Error("forbidden");

            }
            if (!res.ok) {
                const data = await res.json();
                alert(data.message);
                return;
            }

            setMaterials(await res.json());
        } catch (err) {
            console.error("Error fetching materials:", err);
        }
    };

    //-----------------------------------------------------------------------------------------------------------------

    const fetchCategories = async () => {
        try {
            const res = await fetch(`${apiURL}/materials/categories`, {
                method: "GET",
                credentials: "include"
            });

            if (res.status === 401) {
                logout();
                navigate('/login');
                throw new Error("unauthorized");
            }

            if (res.status === 403) {
                navigate('/home');
                throw new Error("forbidden");
            }

            if (!res.ok) {
                const data = await res.json();
                alert(data.message);
                return;
            }

            setCategories(await res.json());
        } catch (err) {
            console.error("Error fetching categories:", err);
        }
    };

    //-----------------------------------------------------------------------------------------------------------------

    const handleAddMaterial = async (material) => {
        try {
            const formData = new FormData();
            formData.append("DisplayName", material.displayName);
            formData.append("CategoryID", material.categoryID);
            formData.append("MaterialType", material.materialType);
            formData.append("Display", material.display);
            formData.append("FileName", material.file.name);
            formData.append("UploadFile", material.file); 

            const res = await fetch(`${apiURL}/materials/insert-material`, {
                method: "POST",
                credentials: "include",
                body: formData,
            });

            if (res.status === 401) {
                logout();
                navigate('/login');
                throw new Error("unauthorized");
            }

            if (res.status === 403) {
                navigate('/home');
                throw new Error("forbidden");
            }

            if (!res.ok) {
                const data = await res.json();
                alert(data.message);
                return;
            }
                
            setShowAddMaterialModal(false);
            fetchMaterials();
        } catch (err) {
            console.error("Error adding material:", err);
        }
    };

    //-----------------------------------------------------------------------------------------------------------------

    const handleAddCategory = async (category) => {
        try {
            const payload = {
                categoryName: category.categoryName,
                display: category.display
            };

            const res = await fetch(`${apiURL}/materials/add-category?categoryName=${encodeURIComponent(category.categoryName)}`, {
                method: "POST",
                credentials: "include",
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (res.status === 401) {
                logout();
                navigate('/login');
                throw new Error("unauthorized");
            }

            if (res.status === 403) {
                navigate('/home');
                throw new Error("forbidden");
            }

            if (!res.ok) {
                const data = await res.json();
                alert(data.message);
                return;
            }

            fetchCategories();
        } catch (err) {
            console.error("Error adding category:", err);
        }
    };

    //-----------------------------------------------------------------------------------------------------------------

    const handleUpdateMaterial = async (material) => {
        try {

            const formData = new FormData();
            formData.append("DisplayName", material.displayName);
            formData.append("CategoryID", material.categoryID);
            formData.append("MaterialType", material.materialType);
            formData.append("Display", material.display);

            const res = await fetch(`${apiURL}/materials/update-material?materialID=${material.materialID}`, {
                method: "PUT",
                credentials: "include",
                body: formData,
            });

            if (res.status === 401) {
                logout();
                navigate('/login');
                throw new Error("unauthorized");
            }

            if (res.status === 403) {
                navigate('/home');
                throw new Error("forbidden");
            }

            if (!res.ok) {
                const data = await res.json();
                alert(data.message);
                return;
            }

            setEditingMaterial(null);
            fetchMaterials();
        } catch (err) {
            console.error("Error updating material:", err);
        }
    };

    //-----------------------------------------------------------------------------------------------------------------

    const handleUpdateCategory = async (updatedCategory) => {
        try {
            const payload = {
                categoryName: updatedCategory.categoryName,
                display: updatedCategory.display
            };

            const res = await fetch(`${apiURL}/materials/update-category?categoryID=${updatedCategory.categoryID}`, {
                method: "PUT",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(payload),
            });

            if (res.status === 401) {
                logout();
                navigate('/login');
                throw new Error("unauthorized");
            }

            if (res.status === 403) {
                navigate('/home');
                throw new Error("forbidden");
            }

            if (!res.ok) {
                const data = await res.json();
                alert(data.message);
                return;
            }

            setShowEditCategoryModal(false);
            setEditingCategory(null);
            fetchCategories();
        } catch (err) {
            console.error("Error updating category:", err);
        }
    };

    //-----------------------------------------------------------------------------------------------------------------

    const handleDeleteMaterial = async (material) => {
        if (!window.confirm("Are you sure you want to delete this material?")) return;
        try {
            const res = await fetch(`${apiURL}/materials/delete-material?MaterialID=${material.materialsID}`, {
                method: "DELETE",
                credentials: "include",
            });

            if (res.status === 401) {
                logout();
                navigate('/login');
                throw new Error("unauthorized");
            }

            if (res.status === 403) {
                navigate('/home');
                throw new Error("forbidden");
            }

            if (!res.ok) {
                const data = await res.json();
                alert(data.message);
                return;
            }

            fetchMaterials();
        } catch (err) {
            console.error("Error deleting material:", err);
        }
    };

    //-----------------------------------------------------------------------------------------------------------------

    const handleDeleteCategory = async (categoryID) => {
        if (!window.confirm("Are you sure you want to delete this category?")) return;
        try {
            const res = await fetch(`${apiURL}/materials/delete-category?CategoryID=${categoryID}`, {
                method: "DELETE",
                credentials: "include",

            });

            if (res.status === 401) {
                logout();
                navigate('/login');
                throw new Error("unauthorized");
            }

            if (res.status === 403) {
                navigate('/home');
                throw new Error("forbidden");
            }
            
            if (!res.ok) {
                const data = await res.json();
                alert(data.message);
                return;
            }

            fetchCategories();
        } catch (err) {
            console.error("Error deleting category:", err);
        }
    };

    //-----------------------------------------------------------------------------------------------------------------

    const getCategoryName = (categoryID) =>
        categories.find((c) => c.categoryID === categoryID)?.categoryName || categoryID;

    return (
        <div className="admin-materials-page">
            <div className="admin-materials-grid">
                {/* Categories Table */}
                <div className="table-card">
                    <div className="table-header">
                        <h3>Categories</h3>
                        <button className="baseButton" onClick={() => setShowAddCategoryModal(true)}>Add Category</button>
                    </div>
                    <table className="paginated-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Display</th>
                                <th>Edit</th>
                                <th>Delete</th>
                            </tr>
                        </thead>
                        <tbody>
                            {categories.map((cat) => (
                                <tr key={cat.categoryID}>
                                    <td>{cat.categoryName}</td>
                                    <td>{cat.display ? "Yes" : "No"}</td>
                                    <td><button className="btn-link" onClick={() => { setEditingCategory(cat); setShowEditCategoryModal(true); }}>Edit</button></td>
                                    <td><button className="btn-link" onClick={() => handleDeleteCategory(cat.categoryID)}>Delete</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Materials Table */}
                <div className="table-card">
                    <div className="table-header">
                        <h3>Materials</h3>
                        <button className="baseButton" onClick={() => setShowAddMaterialModal(true)}>Upload Material</button>
                    </div>
                    <table className="paginated-table">
                        <thead>
                            <tr>
                                <th>File Name</th>
                                <th>Category</th>
                                <th>Display</th>
                                <th>Type</th>
                                <th>Edit</th>
                                <th>Delete</th>
                            </tr>
                        </thead>
                        <tbody>
                            {materials.map((mat) => (
                                <tr key={mat.materialsID}>
                                    <td>{mat.displayName}</td>
                                    <td>{getCategoryName(mat.categoryID)}</td>
                                    <td>{mat.display ? "Yes" : "No"}</td>
                                    <td>{mat.materialType}</td>
                                    <td><button className="btn-link" onClick={() => setEditingMaterial(mat)}>Edit</button></td>
                                    <td><button className="btn-link" onClick={() => handleDeleteMaterial(mat)}>Delete</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modals */}
            <AddMaterialModal
                isOpen={showAddMaterialModal}
                categories={categories}
                onClose={() => setShowAddMaterialModal(false)}
                onSaveMaterial={handleAddMaterial}
                onAddCategory={handleAddCategory}
            />
            <EditMaterialModal
                isOpen={!!editingMaterial}
                material={editingMaterial}
                categories={categories}
                onClose={() => setEditingMaterial(null)}
                onUpdate={handleUpdateMaterial}
            />
            <EditCategoryModal
                isOpen={showEditCategoryModal && !!editingCategory}
                category={editingCategory}
                onClose={() => { setShowEditCategoryModal(false); setEditingCategory(null); }}
                onUpdate={handleUpdateCategory}
            />
            <AddCategoryModal
                isOpen={showAddCategoryModal}
                onClose={() => setShowAddCategoryModal(false)}
                onAdd={handleAddCategory}
            />
        </div>
    );
};

export default AdminMaterialsSection;
